/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Wed Jan 24 2024
 **/

import { ComponentProps, useMemo, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { Loader, Row } from "@/frameworks/designStuff/components";
import { Maybe, SwitchCase } from "@/frameworks/libraries/components";

import withRenderDetect from "@/frameworks/core/utils/withRenderDetect";
import { isNotSupportPreview } from "@/frameworks/core/utils";
import { useUsers } from "@/frameworks/core/API";
import { useFileExtensions } from "@/frameworks/core/utils";
import { usePersistUser } from "@/frameworks/core/persistence";
import { UnDraggable } from "@/frameworks/libraries/components/Dragger";
import { ZIndex } from "@/frameworks/core/commons";
import { Body, ViewerWrap } from "./private/emotion";
import {
	EmptyViewer,
	ImageViewer,
	ImageViewerMobile,
	VideoViewer,
	VideoViewerMobile,
	AudioViewer,
	FileViewerMobile,
	FileViewer
} from "./private/viewer";
import HeaderWrapper from "./private/header/HeaderWrapper";
import MinimizeDraggableWrapper from "./private/minimizeDraggable/MinimizeDraggableWrapper";

type CommonFileHeaderProps = {
	createByID: string;
	createName?: string; // 모바일용 - 모바일에서 webView 시 useUser가 undefined여서 name을 직접넘겨주도록함.
	startIndex?: number;
	date?: number;
	fileName: string[];
	fileDisplaySize?: string[];
	isMobile?: boolean;
	ownerID?: string;
	onDownload: (fileID?: string) => void;
	onRemove: (fileID?: string) => void;
	onClose: (fileID?: string) => void;
	onMinimize?: (minimized: boolean) => void;
	openByWhiteboard?: boolean;
};

type DocumentViewerProps = CommonFileHeaderProps &
	CommonViewerProps &
	Partial<ComponentProps<typeof ImageViewer>> &
	Omit<Partial<ComponentProps<typeof VideoViewer>>, "fileID"> &
	Omit<Partial<ComponentProps<typeof FileViewer>>, "fileID">;

type CommonViewerProps = {
	fileID: string[];
	mimeType: string;
};

function DocumentViewer({
	createByID,
	createName,
	fileID,
	mimeType,
	imageList,
	startIndex = 0,
	date,
	fileName,
	fileDisplaySize,
	isMobile,
	ownerID,
	openByWhiteboard = false,
	...actions
}: DocumentViewerProps) {
	const [targetIndex, setTargetIndex] = useState(startIndex);
	const [minimize, setMinimize] = useState(false);
	const { getFileExtension } = useFileExtensions();
	const user = usePersistUser(createByID);
	const userAPI = useUsers();
	const fileMimeType = mimeType === "video/quicktime" ? "video/mp4" : mimeType;

	const { data: myData } = useQuery({
		queryKey: ["userInfo"],
		queryFn: async () => (await userAPI.me.inquiry.execute())?.data
	});

	const changePreview = () => {
		if (targetIndex > 0) {
			setTargetIndex((prev) => prev - 1);
		}
	};

	const changeNext = () => {
		if (!imageList) return;
		if (targetIndex < imageList.length - 1) {
			setTargetIndex((prev) => prev + 1);
		}
	};

	const handleAction = (type: "onClose" | "onDownload" | "onRemove") => {
		actions[type](fileID?.[targetIndex]);
	};

	const createdByMe = useMemo(() => {
		if (myData) return myData.userId === createByID;
		else return false;
	}, [myData, createByID]);

	const sharedByMe = useMemo(() => {
		if (!ownerID || !myData) return true;
		return myData.userId === ownerID;
	}, [myData, ownerID]);

	const isEmpty = !mimeType || !fileID;

	return (
		<MinimizeDraggableWrapper
			minimize={minimize}
			ratio={0.7}
			zIndex={ZIndex.FE_WHITEBOARD}
			displayName={DocumentViewer.displayName}
		>
			<ViewerWrap id="viewer-wrap" className={minimize ? "minimize" : ""}>
				<Maybe test={!isEmpty} fallback={<Loader />}>
					<HeaderWrapper
						createByID={createByID}
						fileName={fileName?.[targetIndex]}
						publisherName={user?.name || createName || ""}
						date={date}
						fileDisplaySize={fileDisplaySize?.[targetIndex]}
						minimize={minimize}
						mimeType={getFileExtension(mimeType)}
						{...(sharedByMe && {
							onClickClose: () => {
								handleAction("onClose");
							}
						})}
						onClickDownload={() => {
							handleAction("onDownload");
						}}
						{...(createdByMe &&
							!ownerID && {
								onClickDelete: () => {
									handleAction("onRemove");
								}
							})}
						{...(openByWhiteboard && {
							onClickMinimize: () => {
								setMinimize((prev) => !prev);
								actions.onMinimize?.(!minimize);
							}
						})}
					/>
					<UnDraggable>
						<Body>
							<Row
								verticalAlign="center"
								horizonAlign="center"
								css={{ overflow: "hidden", width: "100%", height: "100%", cursor: "auto" }}
							>
								<Maybe test={!isNotSupportPreview({ mimeType })} fallback={<EmptyViewer />}>
									<SwitchCase
										condition={getFileExtension(mimeType)}
										cases={{
											image: isMobile ? (
												<ImageViewerMobile
													imageList={imageList ?? []}
													currentIndex={targetIndex}
													handleChangePreview={changePreview}
													handleChangeNext={changeNext}
												/>
											) : (
												<ImageViewer
													imageList={imageList ?? []}
													currentIndex={targetIndex}
													handleChangePreview={changePreview}
													handleChangeNext={changeNext}
													minimize={minimize}
												/>
											),
											video: isMobile ? (
												<VideoViewerMobile
													fileID={fileID[targetIndex]}
													mimeType={fileMimeType}
													minimize={minimize}
												/>
											) : (
												<VideoViewer
													fileID={fileID[targetIndex]}
													mimeType={fileMimeType}
													minimize={minimize}
												/>
											),
											audio: <AudioViewer fileID={fileID[targetIndex]} mimeType={fileMimeType} />
										}}
										defaultCase={
											isMobile ? (
												<FileViewerMobile fileID={fileID[targetIndex]} />
											) : (
												<FileViewer fileID={fileID[targetIndex]} minimize={minimize} />
											)
										}
									/>
								</Maybe>
							</Row>
						</Body>
					</UnDraggable>
				</Maybe>
			</ViewerWrap>
		</MinimizeDraggableWrapper>
	);
}

DocumentViewer.displayName = "DocumentViewer";
export default withRenderDetect(DocumentViewer);
